import React, { useEffect, useMemo, useState } from 'react';
import { Check, Image, Loader2, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

export type StockImageOption = {
  key: string;
  url: string;
  name: string;
};

interface StockImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUseImages: (keys: string[]) => Promise<void>;
  maxSelectable: number;
}

const StockImagesModal: React.FC<StockImagesModalProps> = ({
  isOpen,
  onClose,
  onUseImages,
  maxSelectable,
}) => {
  const [images, setImages] = useState<StockImageOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedKeys([]);
    setSearchTerm('');
    setLoadError(null);

    const loadImages = async () => {
      setLoading(true);
      try {
        const stockImages = await apiService.getStockImages();
        setImages(stockImages);
      } catch (error: any) {
        console.error('Error loading stock images:', error);
        setLoadError(error?.response?.data?.message || 'Unable to load stock images right now.');
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    void loadImages();
  }, [isOpen]);

  const filteredImages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return images;
    return images.filter((image) => image.name.toLowerCase().includes(query));
  }, [images, searchTerm]);

  const toggleSelection = (key: string) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) {
        return prev.filter((item) => item !== key);
      }
      if (prev.length >= maxSelectable) {
        toast.error(
          maxSelectable === 1
            ? 'You can select 1 more image.'
            : `You can select up to ${maxSelectable} more image(s).`
        );
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleUseImages = async () => {
    if (selectedKeys.length === 0) return;

    setSubmitting(true);
    try {
      await onUseImages(selectedKeys);
      onClose();
    } catch (error: any) {
      console.error('Error using stock images:', error);
      toast.error(error?.response?.data?.message || 'Failed to add selected stock images.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[1px]"
        aria-label="Close stock images dialog"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        <div className="border-b border-gray-200 px-5 py-4 md:px-6 md:py-5">
          <div className="relative">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-0 top-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 sm:hidden"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-1 items-start gap-2 pr-8 sm:items-center sm:pr-0">
                <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0B4778]/10 text-[#0B4778]">
                  <Image className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 md:text-xl">Browse Stock Images</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="hidden shrink-0 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 sm:inline-flex"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-1 text-sm text-gray-600 sm:pl-12">
              Choose professional images for your public gallery. You can add up to {maxSelectable} more.
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search stock images..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-[#0B4778] focus:ring-2 focus:ring-[#0B4778]"
              />
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">{selectedKeys.length}</span> selected
              {maxSelectable > 0 ? (
                <>
                  {' '}
                  · <span className="font-medium text-gray-900">{maxSelectable}</span> remaining
                </>
              ) : (
                <> · gallery full</>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6">
          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center">
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <Loader2 className="h-5 w-5 animate-spin text-[#0B4778]" />
                Loading stock images...
              </div>
            </div>
          ) : loadError ? (
            <div className="flex min-h-[280px] items-center justify-center">
              <div className="max-w-md rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-700">
                {loadError}
              </div>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="flex min-h-[280px] items-center justify-center text-sm text-gray-500">
              {searchTerm.trim() ? 'No stock images match your search.' : 'No stock images are available right now.'}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {filteredImages.map((image) => {
                const isSelected = selectedKeys.includes(image.key);
                const isDisabled = !isSelected && selectedKeys.length >= maxSelectable;

                return (
                  <button
                    key={image.key}
                    type="button"
                    onClick={() => !isDisabled && toggleSelection(image.key)}
                    disabled={isDisabled}
                    className={`group relative overflow-hidden rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-[#0B4778] ring-2 ring-[#0B4778]/30'
                        : isDisabled
                          ? 'cursor-not-allowed border-gray-200 opacity-50'
                          : 'border-gray-200 hover:border-[#0B4778]/40 hover:shadow-md'
                    }`}
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                        loading="lazy"
                      />
                    </div>
                    <div className="border-t border-gray-100 bg-white px-3 py-2">
                      <p className="truncate text-xs font-medium text-gray-800">{image.name}</p>
                    </div>
                    {isSelected && (
                      <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#0B4778] text-white shadow-md">
                        <Check className="h-4 w-4" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-end md:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="aacore-btn2-outline inline-flex items-center justify-center gap-2 bg-white disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleUseImages()}
            disabled={submitting || selectedKeys.length === 0 || maxSelectable <= 0}
            className="aacore-btn2-primary inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding Images...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Use Images
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockImagesModal;
